import { useState, FormEvent } from "react";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";

type FormStatus = "idle" | "sending" | "success" | "error";

export default function Contact() {
  const [status, setStatus] = useState<FormStatus>("idle");
  const [charCount, setCharCount] = useState(0);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const textarea = form.querySelector("textarea");
    if (textarea && textarea.value.length > 500) {
      return;
    }
    setStatus("sending");
    const data = new URLSearchParams(new FormData(form) as unknown as Record<string, string>);
    try {
      const res = await fetch("https://readdy.ai/api/form/d7cg1bfsch5bvrijf2j0", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: data.toString(),
      });
      if (res.ok) {
        setStatus("success");
        form.reset();
        setCharCount(0);
      } else {
        setStatus("error");
      }
    } catch {
      setStatus("error");
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />

      {/* Hero */}
      <section className="pt-40 pb-16 bg-[#0A2540]">
        <div className="max-w-7xl mx-auto px-6 md:px-10 text-center">
          <span className="inline-block px-4 py-1.5 rounded-full border border-white/20 text-white/60 text-xs uppercase tracking-widest mb-6">
            Contact
          </span>
          <h1 className="text-4xl md:text-6xl font-bold text-white mb-4">Contactez-nous</h1>
          <p className="text-white/60 text-lg">Pour toute demande, nous sommes à votre écoute.</p>
        </div>
      </section>

      {/* Main */}
      <section className="py-20 bg-white flex-1">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="flex flex-col lg:flex-row gap-12">
            {/* Form */}
            <div className="flex-1">
              <h2 className="text-2xl font-bold text-[#0A2540] mb-2">Envoyez-nous un message</h2>
              <p className="text-gray-400 text-sm mb-8">Nous vous répondrons dans les meilleurs délais.</p>

              {status === "success" && (
                <div className="mb-6 p-4 rounded-xl bg-green-50 border border-green-200 text-green-700 text-sm flex items-center gap-3">
                  <span className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-checkbox-circle-line text-lg"></i>
                  </span>
                  Votre message a bien été envoyé. Nous vous répondrons rapidement.
                </div>
              )}
              {status === "error" && (
                <div className="mb-6 p-4 rounded-xl bg-red-50 border border-red-200 text-red-600 text-sm flex items-center gap-3">
                  <span className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-error-warning-line text-lg"></i>
                  </span>
                  Une erreur est survenue. Veuillez réessayer ou nous contacter par email.
                </div>
              )}

              <form
                data-readdy-form
                id="contact-bobrowska"
                onSubmit={handleSubmit}
                className="flex flex-col gap-6"
              >
                <div className="flex flex-col gap-1">
                  <label className="text-xs uppercase tracking-widest text-gray-400">Nom *</label>
                  <input
                    type="text"
                    name="nom"
                    required
                    placeholder="Votre nom complet"
                    className="border-b-2 border-gray-200 focus:border-[#0A2540] outline-none py-3 text-sm text-gray-700 placeholder-gray-300 bg-transparent transition-colors duration-200"
                  />
                </div>
                <div className="flex flex-col gap-1">
                  <label className="text-xs uppercase tracking-widest text-gray-400">Email *</label>
                  <input
                    type="email"
                    name="email"
                    required
                    placeholder="votre@email.com"
                    className="border-b-2 border-gray-200 focus:border-[#0A2540] outline-none py-3 text-sm text-gray-700 placeholder-gray-300 bg-transparent transition-colors duration-200"
                  />
                </div>
                <div className="flex flex-col gap-1">
                  <label className="text-xs uppercase tracking-widest text-gray-400">Téléphone</label>
                  <input
                    type="tel"
                    name="telephone"
                    placeholder="+33 6 00 00 00 00"
                    className="border-b-2 border-gray-200 focus:border-[#0A2540] outline-none py-3 text-sm text-gray-700 placeholder-gray-300 bg-transparent transition-colors duration-200"
                  />
                </div>
                <div className="flex flex-col gap-1">
                  <label className="text-xs uppercase tracking-widest text-gray-400">
                    Description de la demande *
                  </label>
                  <textarea
                    name="message"
                    required
                    rows={5}
                    maxLength={500}
                    placeholder="Décrivez votre projet ou votre demande..."
                    onChange={(e) => setCharCount(e.target.value.length)}
                    className="border-b-2 border-gray-200 focus:border-[#0A2540] outline-none py-3 text-sm text-gray-700 placeholder-gray-300 bg-transparent transition-colors duration-200 resize-none"
                  />
                  <span className={`text-xs text-right mt-1 ${charCount > 480 ? "text-red-400" : "text-gray-300"}`}>
                    {charCount}/500
                  </span>
                </div>
                <button
                  type="submit"
                  disabled={status === "sending"}
                  className="self-start inline-flex items-center gap-2 px-8 py-4 rounded-full bg-[#0A2540] text-white font-semibold text-sm hover:bg-[#0d3060] transition-all duration-200 disabled:opacity-60 whitespace-nowrap cursor-pointer"
                >
                  {status === "sending" ? (
                    <>
                      <span className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-loader-4-line animate-spin"></i>
                      </span>
                      Envoi en cours...
                    </>
                  ) : (
                    <>
                      Envoyer
                      <span className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-send-plane-line"></i>
                      </span>
                    </>
                  )}
                </button>
              </form>
            </div>

            {/* Info */}
            <div className="lg:w-2/5 bg-[#f8f9fb] rounded-2xl p-10 flex flex-col gap-8">
              <div>
                <h3 className="text-xl font-bold text-[#0A2540] mb-1">BOBROWSKA</h3>
                <p className="text-gray-500 text-sm">Ewa Bobrowska</p>
                <p className="text-gray-400 text-xs mt-1">SIRET : 100 085 687 00018</p>
              </div>

              <div className="w-full h-px bg-gray-200"></div>

              <div className="flex flex-col gap-6">
                <div>
                  <p className="text-xs uppercase tracking-widest text-gray-400 mb-2">Adresse</p>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    49 Rue<br />
                    67130 Wisches<br />
                    France
                  </p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-widest text-gray-400 mb-2">Téléphone</p>
                  <a
                    href="tel:+33605613058"
                    className="text-[#0A2540] font-medium text-sm hover:underline"
                  >
                    +33 6 05 61 30 58
                  </a>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-widest text-gray-400 mb-2">Email</p>
                  <a
                    href="mailto:ewabobroowska@outlook.com"
                    className="text-[#0A2540] font-medium text-sm hover:underline break-all"
                  >
                    ewabobroowska@outlook.com
                  </a>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-widest text-gray-400 mb-2">Activité</p>
                  <p className="text-gray-600 text-sm">70.22Z — Conseil pour les affaires et autres conseils de gestion</p>
                </div>
              </div>

              {/* Map */}
              <div className="rounded-xl overflow-hidden w-full h-48">
                <iframe
                  title="Localisation BOBROWSKA Wisches"
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2647.0!2d7.2!3d48.5!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x4796c0!2sWisches%2C%2067130%2C%20France!5e0!3m2!1sfr!2sfr!4v1"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                ></iframe>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
